import { useState, useCallback } from 'react';

// Helper function to calculate distance between two coordinates
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI/180;
    const φ2 = lat2 * Math.PI/180;
    const Δφ = (lat2-lat1) * Math.PI/180;
    const Δλ = (lon2-lon1) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance in meters
};

// Helper function to find the closest step to user's current location
const findCurrentStep = (userLocation, routeSteps) => {
    if (!userLocation || !routeSteps || routeSteps.length === 0) {
        console.log('🗺️ [Current Step] Missing data:', {
            hasUserLocation: !!userLocation,
            hasRouteSteps: !!routeSteps,
            stepsLength: routeSteps?.length
        });
        return 0;
    }

    let closestStepIndex = 0;
    let minDistance = Infinity;

    // For each step, find the distance to the user's current location
    routeSteps.forEach((step, index) => {
        if (step.path && step.path.length > 0) {
            // Check distance to the start of this step
            const stepStart = step.path[0];
            if (stepStart && stepStart.latitude && stepStart.longitude) {
                const distance = calculateDistance(
                    userLocation.latitude, 
                    userLocation.longitude,
                    stepStart.latitude, 
                    stepStart.longitude
                );

                console.log(`🗺️ [Step ${index}] Distance: ${Math.round(distance)}m`, {
                    stepStart: stepStart,
                    instructions: step.instructions,
                    maneuver: step.maneuver
                });

                if (distance < minDistance) {
                    minDistance = distance;
                    closestStepIndex = index;
                }
            } else {
                console.log(`🗺️ [Step ${index}] Invalid step start:`, stepStart);
            }
        } else {
            console.log(`🗺️ [Step ${index}] No path data:`, step);
        }
    });

    console.log('🗺️ [Current Step Calculation]', {
        userLocation,
        totalSteps: routeSteps.length,
        currentStepIndex: closestStepIndex,
        minDistance: Math.round(minDistance),
        selectedStep: routeSteps[closestStepIndex]
    });

    return closestStepIndex;
};

const useRouteManager = (map, onRouteUpdate) => {
    const [currentRoute, setCurrentRoute] = useState(null);
    const [routeOverlay, setRouteOverlay] = useState(null);
    const [routeMarkers, setRouteMarkers] = useState([]);
    const [userLocation, setUserLocation] = useState(null);

    const clearRoute = useCallback(() => {
        console.log('🧹 [Clearing Route]', {
            hasOverlay: !!routeOverlay,
            numMarkers: routeMarkers.length,
            totalMapOverlays: map?.overlays?.length || 0
        });

        // Remove route overlay with enhanced checking
        if (routeOverlay && map) {
            try {
                map.removeOverlay(routeOverlay);
                console.log('✅ [Route Overlay Removed]');
            } catch (error) {
                console.error('❌ [Route Overlay Removal Error]', error);
            }
            setRouteOverlay(null);
        }

        // Remove all route-related overlays (in case of duplicates)
        if (map && map.overlays) {
            const routeOverlays = map.overlays.filter(overlay => 
                overlay instanceof window.mapkit.PolylineOverlay
            );
            
            if (routeOverlays.length > 0) {
                console.log(`🧹 [Removing ${routeOverlays.length} Route Overlays]`);
                routeOverlays.forEach(overlay => {
                    try {
                        map.removeOverlay(overlay);
                    } catch (error) {
                        console.error('❌ [Overlay Removal Error]', error);
                    }
                });
            }
        }

        // Remove route markers
        if (routeMarkers.length > 0 && map) {
            routeMarkers.forEach(marker => {
                try {
                    map.removeAnnotation(marker);
                } catch (error) {
                    console.error('❌ [Marker Removal Error]', error);
                }
            });
            setRouteMarkers([]);
        }

        setCurrentRoute(null);
        setUserLocation(null);
        onRouteUpdate?.(null);
        
        console.log('✅ [Route Cleared]', {
            overlaysAfter: map?.overlays?.length || 0,
            annotationsAfter: map?.annotations?.length || 0
        });
    }, [map, routeOverlay, routeMarkers, onRouteUpdate]);

    const createRoute = useCallback(async (from, to) => {
        console.log('🛣️ [createRoute]', { from, to });
        
        if (!map || !window.mapkit) {
            console.error('❌ [Route Error] Missing map or mapkit');
            return null;
        }

        clearRoute();
        setUserLocation(from);

        try {
            const directions = new window.mapkit.Directions();
            const fromPoint = new window.mapkit.Coordinate(from.latitude, from.longitude);
            const toPoint = new window.mapkit.Coordinate(to.latitude, to.longitude);

            console.log('🚗 [Calculating Route]', {
                from: fromPoint,
                to: toPoint
            });

            const route = await new Promise((resolve, reject) => {
                directions.route(
                    {
                        origin: fromPoint,
                        destination: toPoint,
                        transportType: window.mapkit.Directions.Transport.Automobile
                    },
                    (error, data) => {
                        if (error) {
                            console.error('❌ [Route Error]', error);
                            reject(error);
                        } else {
                            resolve(data.routes[0]);
                        }
                    }
                );
            });

            // Create destination marker only (user location is handled by LocationManager)
            const endMarker = new window.mapkit.MarkerAnnotation(toPoint, {
                title: to.title || "Destination",
                subtitle: to.subtitle || "",
                glyphText: "🎯",
                color: "#7C3AED",
                displayPriority: 900
            });

            // Get all points from the route
            const points = route._path.reduce((acc, segment) => {
                return [...acc, ...segment];
            }, []);

            if (points.length === 0) {
                throw new Error('No points available for route');
            }

            // Create route overlay
            const coordinates = points.map(point => new window.mapkit.Coordinate(point.latitude, point.longitude));
            const newRouteOverlay = new window.mapkit.PolylineOverlay(coordinates);
            
            // Apply styles
            newRouteOverlay.style.strokeColor = "#7C3AED";
            newRouteOverlay.style.lineWidth = 8;
            newRouteOverlay.style.strokeOpacity = 1;
            newRouteOverlay.style.lineJoin = "round";
            newRouteOverlay.style.lineCap = "round";

            // Add overlay and marker
            map.addOverlay(newRouteOverlay);
            map.addAnnotation(endMarker);

            // Save references
            setRouteMarkers([endMarker]);
            setRouteOverlay(newRouteOverlay);

            // Create route info with enhanced step tracking
            const routeSteps = route.steps.map((step, index) => {
                const maneuverType = step._imageUrl?.match(/maneuverType=(\d+)/)?.[1];
                const stepPath = route._path[index] || [];
                
                const stepData = {
                    path: stepPath,
                    instructions: step.instructions || '',
                    distance: step.distance || 0,
                    maneuver: maneuverType || '3',
                    stepIndex: index
                };

                console.log(`🛣️ [Step ${index}] Created:`, {
                    instructions: stepData.instructions,
                    maneuver: stepData.maneuver,
                    distance: stepData.distance,
                    pathLength: stepData.path.length,
                    hasValidPath: stepData.path.length > 0 && stepData.path[0]?.latitude
                });

                return stepData;
            });

            console.log('🛣️ [All Route Steps]', {
                totalSteps: routeSteps.length,
                firstStepInstructions: routeSteps[0]?.instructions,
                firstStepManeuver: routeSteps[0]?.maneuver,
                firstStepPathLength: routeSteps[0]?.path?.length
            });

            // Find current step based on user location
            const currentStepIndex = findCurrentStep(from, routeSteps);
            const currentStep = routeSteps[currentStepIndex] || routeSteps[0];

            const routeInfo = {
                steps: routeSteps,
                expectedTravelTime: route.expectedTravelTime,
                distance: route.distance,
                name: route.name,
                currentStepIndex,
                nextManeuver: currentStep.maneuver,
                instruction: currentStep.instructions,
                userLocation: from
            };

            console.log('📊 [Route Info]', {
                totalSteps: routeSteps.length,
                currentStepIndex,
                nextManeuver: routeInfo.nextManeuver,
                nextInstruction: routeInfo.instruction,
                distance: routeInfo.distance,
                time: routeInfo.expectedTravelTime
            });

            setCurrentRoute(routeInfo);
            onRouteUpdate?.(routeInfo);

            // Notify that route overlay has been updated (for LocationManager to preserve location marker)
            console.log('📡 [Dispatching Route Overlay Updated Event]');
            window.dispatchEvent(new CustomEvent('routeOverlayUpdated', {
                detail: { 
                    userLocation: from,
                    preserveLocationMarker: true 
                }
            }));

            return routeInfo;
        } catch (error) {
            console.error('❌ [Route Creation Error]', error);
            clearRoute();
            return null;
        }
    }, [map, clearRoute, onRouteUpdate]);

    const updateRoute = useCallback(async (from, to, forceRecalculate = false) => {
        console.log('🔄 [updateRoute]', { from, to, forceRecalculate });
        
        if (!map || !window.mapkit) {
            console.error('❌ [Route Update Error] Missing map or mapkit');
            return null;
        }

        // Store updated user location
        setUserLocation(from);
        
        // Emit event to update location marker position without recreating it
        window.dispatchEvent(new CustomEvent('forceLocationUpdate', {
            detail: { location: from }
        }));

        // Always prefer fast updates to prevent flickering
        if (currentRoute && currentRoute.steps) {
            const currentStepIndex = findCurrentStep(from, currentRoute.steps);
            const currentStep = currentRoute.steps[currentStepIndex] || currentRoute.steps[0];

            // Create updated route info with new current step
            const updatedRouteInfo = {
                ...currentRoute,
                currentStepIndex,
                nextManeuver: currentStep.maneuver,
                instruction: currentStep.instructions,
                userLocation: from,
                steps: currentRoute.steps.map((step, index) => ({
                    ...step,
                    distance: index === currentStepIndex ? step.distance : step.distance // You could calculate remaining distance here
                }))
            };

            console.log('⚡ [Fast Route Update]', {
                totalSteps: currentRoute.steps.length,
                currentStepIndex,
                nextManeuver: updatedRouteInfo.nextManeuver,
                nextInstruction: updatedRouteInfo.instruction,
                stepDistance: currentStep.distance
            });

            setCurrentRoute(updatedRouteInfo);
            onRouteUpdate?.(updatedRouteInfo);

            return updatedRouteInfo;
        }

        // Only recalculate entire route if we don't have current route data or force is requested
        if (!currentRoute || !currentRoute.steps || forceRecalculate) {
        try {
            console.log('🛣️ [Recalculating Full Route]', {
                from: from,
                to: to,
                currentOverlays: map?.overlays?.length || 0
            });

            // First, properly clear ALL existing route data to prevent overlap
            console.log('🧹 [Clearing All Route Data Before Update]');
            
            // Use the enhanced clearRoute but preserve user location
            const tempUserLocation = userLocation;
            
            // Remove route overlay with enhanced checking
            if (routeOverlay && map) {
                try {
                    map.removeOverlay(routeOverlay);
                    console.log('✅ [Old Route Overlay Removed]');
                } catch (error) {
                    console.error('❌ [Old Route Overlay Removal Error]', error);
                }
                setRouteOverlay(null);
            }

            // Remove all existing route-related overlays (in case of duplicates)
            if (map && map.overlays) {
                const existingRouteOverlays = map.overlays.filter(overlay => 
                    overlay instanceof window.mapkit.PolylineOverlay
                );
                
                if (existingRouteOverlays.length > 0) {
                    console.log(`🧹 [Removing ${existingRouteOverlays.length} Existing Route Overlays]`);
                    existingRouteOverlays.forEach(overlay => {
                        try {
                            map.removeOverlay(overlay);
                        } catch (error) {
                            console.error('❌ [Existing Overlay Removal Error]', error);
                        }
                    });
                }
            }

            // Remove old route markers
            if (routeMarkers.length > 0 && map) {
                routeMarkers.forEach(marker => {
                    try {
                        map.removeAnnotation(marker);
                    } catch (error) {
                        console.error('❌ [Old Marker Removal Error]', error);
                    }
                });
                setRouteMarkers([]);
            }

            // Restore user location after clearing
            setUserLocation(tempUserLocation);

            const directions = new window.mapkit.Directions();
            const fromPoint = new window.mapkit.Coordinate(from.latitude, from.longitude);
            const toPoint = new window.mapkit.Coordinate(to.latitude, to.longitude);

            console.log('🚗 [Recalculating Route]', {
                from: fromPoint,
                to: toPoint
            });

            const route = await new Promise((resolve, reject) => {
                directions.route(
                    {
                        origin: fromPoint,
                        destination: toPoint,
                        transportType: window.mapkit.Directions.Transport.Automobile
                    },
                    (error, data) => {
                        if (error) {
                            console.error('❌ [Route Update Error]', error);
                            reject(error);
                        } else {
                            resolve(data.routes[0]);
                        }
                    }
                );
            });

            // Get all points from the new route
            const points = route._path.reduce((acc, segment) => {
                return [...acc, ...segment];
            }, []);

            if (points.length === 0) {
                throw new Error('No points available for updated route');
            }

            // Create NEW route overlay
            const coordinates = points.map(point => new window.mapkit.Coordinate(point.latitude, point.longitude));
            const newRouteOverlay = new window.mapkit.PolylineOverlay(coordinates);
            
            // Apply styles to new overlay
            newRouteOverlay.style.strokeColor = "#7C3AED";
            newRouteOverlay.style.lineWidth = 8;
            newRouteOverlay.style.strokeOpacity = 1;
            newRouteOverlay.style.lineJoin = "round";
            newRouteOverlay.style.lineCap = "round";

            // Create new destination marker
            const endMarker = new window.mapkit.MarkerAnnotation(toPoint, {
                title: to.title || "Destination",
                subtitle: to.subtitle || "",
                glyphText: "🎯",
                color: "#7C3AED",
                displayPriority: 900
            });

            // Add NEW overlay and markers to map
            console.log('✅ [Adding New Route Overlay and Markers]');
            map.addOverlay(newRouteOverlay);
            map.addAnnotation(endMarker);

            // Save references to new elements
            setRouteMarkers([endMarker]);
            setRouteOverlay(newRouteOverlay);

            // Notify that route overlay has been updated (for LocationManager to preserve location marker)
            console.log('📡 [Dispatching Route Overlay Updated Event]');
            window.dispatchEvent(new CustomEvent('routeOverlayUpdated', {
                detail: { 
                    userLocation: from,
                    preserveLocationMarker: true 
                }
            }));

            // Update route info with new steps and maneuvers
            const routeSteps = route.steps.map((step, index) => {
                const maneuverType = step._imageUrl?.match(/maneuverType=(\d+)/)?.[1];
                const stepPath = route._path[index] || [];
                
                const stepData = {
                    path: stepPath,
                    instructions: step.instructions || '',
                    distance: step.distance || 0,
                    maneuver: maneuverType || '3',
                    stepIndex: index
                };

                console.log(`🔄 [Update Step ${index}] Created:`, {
                    instructions: stepData.instructions,
                    maneuver: stepData.maneuver,
                    distance: stepData.distance,
                    pathLength: stepData.path.length,
                    hasValidPath: stepData.path.length > 0 && stepData.path[0]?.latitude
                });

                return stepData;
            });

            console.log('🔄 [Updated Route Steps]', {
                totalSteps: routeSteps.length,
                firstStepInstructions: routeSteps[0]?.instructions,
                firstStepManeuver: routeSteps[0]?.maneuver,
                firstStepPathLength: routeSteps[0]?.path?.length
            });

            // Find current step based on user location
            const currentStepIndex = findCurrentStep(from, routeSteps);
            const currentStep = routeSteps[currentStepIndex] || routeSteps[0];

            const routeInfo = {
                steps: routeSteps,
                expectedTravelTime: route.expectedTravelTime,
                distance: route.distance,
                name: route.name,
                currentStepIndex,
                nextManeuver: currentStep.maneuver,
                instruction: currentStep.instructions,
                userLocation: from
            };

            console.log('📊 [Updated Route Info]', {
                totalSteps: routeSteps.length,
                currentStepIndex,
                nextManeuver: routeInfo.nextManeuver,
                nextInstruction: routeInfo.instruction,
                distance: routeInfo.distance,
                time: routeInfo.expectedTravelTime,
                overlayUpdated: true
            });

            setCurrentRoute(routeInfo);
            onRouteUpdate?.(routeInfo);

            return routeInfo;
        } catch (error) {
            console.error('❌ [Route Update Error]', error);
            return null;
        }
        }
        
        // If we're here, we already handled the update in the fast path
        return currentRoute;
    }, [map, currentRoute, onRouteUpdate, routeOverlay, routeMarkers, userLocation]);

    return {
        createRoute,
        updateRoute,
        clearRoute,
        currentRoute
    };
};

export default useRouteManager; 